/**
 * setLang — the ONE interface-language switch primitive.
 *
 * Updates `<html lang>` + `<html dir>`, evicts the stale fetch cache, loads the
 * i18n namespaces for the target language, hands them to smbls polyglot so
 * `{{ key | polyglot }}` resolves, updates the root state so every reactive
 * prop re-evaluates, persists the choice to this device, and (for a real user
 * choice) to the account.
 *
 * ── Why this is a `function` and not an arrow ──────────────────────────────
 * `el.call('setLang', code)` resolves through `context.functions` and invokes
 * `method.call(element, ...args)` (smbls element/src/methods.js). An ARROW
 * function cannot receive that binding — `this` stays the module scope — so the
 * old arrow form threw away the only handle it had on the app. `LangSwitcher`
 * passes no `opts`, so `opts.context` was undefined too: `sdk`, `shellState`
 * and the polyglot bridge below were ALL dead on the switcher path, and a click
 * changed nothing but `<html lang>` and `localStorage`. The framework's own
 * polyglot functions are plain `export function` declarations for exactly this
 * reason. FA207 governs helpers NESTED inside a handler, not an exported
 * context function, so a declaration is correct here.
 *
 * Both call shapes are supported and both exist in the tree:
 *   el.call('setLang', 'ka')                        → `this` is the element
 *   setLang('ka', { context, skipPersist: true })   → cross-tab storage sync
 * If neither yields a context the function WARNS instead of silently doing
 * half the work — a silent half-switch is what produced this whole defect.
 *
 * ── Persistence, and the one rule that must hold ───────────────────────────
 * `localStorage.lang` and `user.preferences.lang` are written ONLY by a real,
 * user-initiated choice. A language the system merely RESOLVED at boot (a
 * workspace default, most of all) is applied to the session and persisted
 * NOWHERE. Otherwise the workspace default stamps itself into `localStorage`,
 * `resolveLang` reads it back as `stored` on the next boot — which outranks
 * `workspaceDefault` — and the admin default has impersonated a user choice
 * that nobody can change back. `transient: true` is that guarantee; it
 * suppresses BOTH persistence sites. `skipPersist` keeps its older, narrower
 * meaning (skip the account write only) because the cross-tab echo in
 * `packages/workspace/wireGlobalListeners.js` relies on it and re-writing
 * `localStorage` there is harmless.
 *
 * ── The workspace-language refusal ─────────────────────────────────────────
 * Inside an active workspace the WORKSPACE's resolved language rules (THE LAW,
 * functions/languages.js `resolveLang`), under 'default' and 'enforced' alike.
 * A non-`transient` switch to any other language is REFUSED and returns
 * `false` — before the document, the bundles, `localStorage` and
 * `sdk.updateMe` — because `applyWorkspaceLangPolicy` would revert it on the
 * next active-workspace event. The visible switchers render locked on the
 * same answer. See the block in the body.
 */

// `smbls` bundles and re-exports these polyglot helpers. Importing them from
// the framework barrel keeps local builds working when the standalone private
// polyglot package is unavailable from the configured registry.
//
// `loadTranslations` is NOT imported any more. Its real signature is
// `loadTranslations(lang)` bound to an element, and it returns immediately
// unless `context.polyglot.fetch` is configured — the shell serves static
// bundles, so it was always a no-op. The old call passed a SECOND argument
// (`mergeAllNamespaces(newLang)`) that the function does not accept, from an
// UNBOUND import whose `this` was undefined, so it exited at its first guard
// (`if (!poly || !root) return`) every single time. The merged strings are
// written straight into `context.polyglot.translations[lang]` below instead —
// lookup step 2 of `translate()`, which needs no binding at all.
import { setLang as polyglotSetLang } from 'smbls'
import {
  i18nStaticTranslations,
  loadI18nNamespaces,
  hasI18nBundle,
  dropNonEnglishCache,
  getLoadedNamespaces
} from './i18nLoader.js'

/**
 * @param {string} newLang            - target ISO language code (must be shipped)
 * @param {object} [opts]
 * @param {object} [opts.context]     - DOMQL context; only needed when `this`
 *                                      is not an element (cross-tab sync, boot)
 * @param {object} [opts.shellState]  - root state, when the caller holds one
 * @param {boolean} [opts.skipPersist] - skip the account (SDK) write
 * @param {boolean} [opts.transient]   - RESOLVED, not chosen: persist nowhere
 * @param {boolean} [opts.redeliver]  - the tree is mounted and the bundles
 *   arrived after it: re-push them so already-rendered props re-evaluate
 * @returns {Promise<boolean>} true when the language actually changed
 */
// FA204 / frank trap — `SUPPORTED` and `RTL_LANGS` are INLINED locals rather
// than reads of `functions/languages.js`'s `isSupportedLang` / `langDir`.
// `functions/` is a frank section barrel: every export becomes its own emitted
// entry, and `languages.js` inlines these same two lists inside each of its own
// functions for precisely this reason (see that file's header). Reading the
// registry across the barrel from inside a serialized export would be the trap
// one level out. The lists are pinned to the registry by
// __tests__/i18nBootLanguage.test.js, which fails if they drift.
export async function setLang(newLang, opts) {
  const SUPPORTED = ['en', 'ka']
  const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur'])
  const o = opts || {}

  // An unshipped code is a NO-OP, never a switch. Honouring one would put the
  // session into a language with no bundles: every key falls through to the
  // English base, the switcher shows a code nothing can render, and nothing
  // says why. Warn and leave the session where it is.
  if (typeof newLang !== 'string' || SUPPORTED.indexOf(newLang) === -1) {
    console.warn(
      `[setLang] '${newLang}' is not a shipped language (${SUPPORTED.join(', ')}) — ignored`
    )
    return false
  }

  // `this` is the element on the `el.call('setLang', code)` path; `opts.context`
  // is the only source on the direct-call path (cross-tab sync, boot).
  const el = this && typeof this.call === 'function' ? this : null
  const context = o.context || el?.context || null
  const shellState = o.shellState || el?.state?.root || context?.state || null

  if (!context) {
    console.warn(
      "[setLang] no DOMQL context — call it as el.call('setLang', code) or pass " +
        '{ context }. The document language is updated but polyglot is not.'
    )
  }

  // ── THE WORKSPACE-LANGUAGE GATE ──────────────────────────────────────────
  // THE LAW (Nika, 2026-09-10; functions/languages.js `resolveLang`): inside
  // the workspace shell the WORKSPACE's resolved language rules — enforced
  // pair → `settings.language` → the white-label brand → English. The member's
  // account preference and this device's stored choice decide nothing there.
  //
  // `applyWorkspaceLangPolicy` re-applies that answer on EVERY active-workspace
  // event (boot, loadPrivateData, a switch, the full-record follow-up). So a
  // switch it does not honour cannot stick: measured live on
  // my.symbols.app/w/aihouse (2026-09-23) — `setLang('en')` painted English,
  // then the policy's `setLang('ka', {transient, redeliver})` painted Georgian
  // again. The page switched and switched back, and the refused choice had
  // already been written to `localStorage` and the account.
  //
  // This gate used to refuse ONLY under `languagePolicy: 'enforced'`. It now
  // refuses a non-transient switch EXACTLY when the policy would revert it:
  // there is an active workspace (`root.workspace`) and the language the
  // policy resolves for it is not `newLang`. Nothing downstream runs — no
  // document write, no bundle load, no `localStorage`, no `sdk.updateMe`.
  //
  //   · No active workspace → no policy runs → no gate. The standalone and
  //     public surfaces (landing, docs, a logged-out page) keep their switch.
  //   · A record still IN FLIGHT (an id, no `settings`, not stamped full, not
  //     marked unreachable — the `pending` state of workspaceSettings.js)
  //     holds the ACTIVE language: the policy skips that record and re-runs on
  //     the full one, so any switch now would be decided by settings nobody has
  //     read yet.
  //
  // The visible switchers lock on the SAME answer (LangSwitcher and
  // InterfaceLanguageField `lockedLang`), so a person does not meet this
  // refusal by clicking. It still matters for the callers that reach no
  // control: the cross-tab `storage` echo in workspace/wireGlobalListeners.js
  // and a module's own switcher that calls `el.call('setLang', …)`.
  //
  // `transient: true` is exempt. That flag means "the system RESOLVED this,
  // it is persisted nowhere" — it is how the policy itself applies the
  // workspace language. Gating it would make the policy unable to apply.
  //
  // FA204 / frank trap — the resolution is INLINED here rather than read off
  // `functions/languages.js`'s `resolveLang` / `enforcedWorkspaceLang`, for
  // exactly the reason `SUPPORTED` and `RTL_LANGS` are inlined above:
  // `functions/` is a frank section barrel and a bare cross-barrel read from
  // inside a serialized export is unresolvable in the eval-revived runtime.
  // Pinned to the policy by __tests__/i18nBootLanguage.test.js, which runs
  // the real `applyWorkspaceLangPolicy` beside this gate and fails on any
  // case where the two disagree.
  const _wsRoot = shellState || null
  const _ws =
    _wsRoot && _wsRoot.workspace && typeof _wsRoot.workspace === 'object' ? _wsRoot.workspace : null
  let _workspaceLang = null
  if (_ws) {
    const _ok = (c) => typeof c === 'string' && SUPPORTED.indexOf(c) !== -1
    const _settings = _ws.settings && typeof _ws.settings === 'object' ? _ws.settings : null
    const _pending =
      !_settings && _ws._shape !== 'full' && !!(_ws.id || _ws._id) && !_ws._completionUnreachable
    if (_pending) {
      const _active = _wsRoot.lang || context?.state?.lang || null
      _workspaceLang = _ok(_active) ? _active : 'en'
    } else {
      const _brand = _wsRoot.whitelabelBrand && _wsRoot.whitelabelBrand.lang
      _workspaceLang =
        (_settings && _settings.languagePolicy === 'enforced' && _ok(_settings.language)
          ? _settings.language
          : null) ||
        (_settings && _ok(_settings.language) ? _settings.language : null) ||
        (_ok(_brand) ? _brand : null) ||
        'en'
    }
  }
  if (!o.transient && _workspaceLang && _workspaceLang !== newLang) {
    console.warn(
      `[setLang] '${newLang}' refused — this workspace renders in '${_workspaceLang}'. ` +
        'The account preference was NOT changed.'
    )
    return false
  }

  // A lazy translation chunk can finish after another choice or a workspace
  // switch. Only the latest request in the same scope may repaint or persist.
  // Keep coordination on the context, outside the user-visible reactive state.
  const requestOwner = context?.polyglot || context || shellState
  const request = {}
  if (requestOwner) requestOwner._languageSwitchRequest = request
  const scopeKey = () =>
    JSON.stringify([
      shellState?.org?.id || shellState?.jwtClaims?.workspace_org_id || '',
      shellState?.workspace?.id || shellState?.workspace?._id || '',
      shellState?.jwtClaims?.active_workspace_id || '',
      shellState?.workspace?.settings?.language || '',
      shellState?.workspace?.settings?.languagePolicy || '',
      shellState?.currentUserId || shellState?.user?.id || shellState?.user?._id || ''
    ])
  const startedIn = scopeKey()
  const isCurrent = () =>
    (!requestOwner || requestOwner._languageSwitchRequest === request) && scopeKey() === startedIn

  // ── Bundles ───────────────────────────────────────────────────────────────
  // ENGLISH IS BUNDLED; EVERY OTHER LANGUAGE IS A LAZY, PER-NAMESPACE CHUNK.
  // Nothing here touches the network on any plane — see i18nLoader.js's header
  // for why (a `.json` asset compiles to a `.js` module under Parcel, and the
  // dev server answers an unknown path with 200 + SPA-fallback HTML, so a
  // runtime fetch of a bundle can only fail silently).
  //
  // The namespace SET is the union of: `common` (always), what bootShell
  // declared for this session from the running app's manifest
  // (`context.polyglot.namespaces`), and every namespace anything has already
  // asked for (`getLoadedNamespaces()`). The chunks load in PARALLEL, so a
  // five-namespace surface is one round of imports, not five serial ones.
  const _static = i18nStaticTranslations()
  const _bundled = _static[newLang]

  const _namespaces = Array.from(
    new Set(['common', ...(context?.polyglot?.namespaces || []), ...getLoadedNamespaces()])
  )
  // Did the registry actually SERVE this language, or is every namespace about
  // to fall back to English? A frank-published record has no chunks to import,
  // and on that plane a baked `translations[lang]` in the payload is the only
  // real translation there is — so a fallback result must never overwrite it.
  const _served = !!_bundled || _namespaces.some((ns) => hasI18nBundle(newLang, ns))

  let _resolved = _bundled
  if (!_bundled) {
    dropNonEnglishCache()
    _resolved = await loadI18nNamespaces(newLang, _namespaces)
  }

  if (!isCurrent()) return false

  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('lang', newLang)
    document.documentElement.setAttribute('dir', RTL_LANGS.has(newLang) ? 'rtl' : 'ltr')
  }

  if (context) {
    if (!context.polyglot) {
      console.warn(
        '[setLang] context.polyglot is not configured — translations cannot be ' +
          'applied. It is declared by @symbo-ls/workspace-shared and merges in ' +
          'through sharedLibraries; bootShell seeds it for a standalone boot.'
      )
    } else {
      const poly = context.polyglot
      if (!poly.translations) poly.translations = {}
      // A non-default language OVERRIDES the English base key by key; anything
      // it is missing still resolves through `translate()`'s default-language
      // fallback, so a partial bundle degrades to English, never to a raw key.
      //
      // The bundle is re-applied on EVERY switch rather than trusted to be
      // there already: a published payload, a standalone boot and a warm shell
      // each seed `translations` from a different place, and a switch that
      // silently depended on one of them is exactly the defect being fixed.
      //
      // WHO WINS ON A COLLISION depends on whether the registry served the
      // language. When it did, the freshly-loaded chunk is the newer truth and
      // goes last. When it did NOT — a published record, where `loadI18nNamespaces`
      // can only hand back the English base — whatever the payload already
      // carries goes last, or a fallback would erase a baked translation.
      const _existing = poly.translations[newLang] || {}
      if (_resolved && Object.keys(_resolved).length) {
        poly.translations[newLang] = _served
          ? { ..._existing, ..._resolved }
          : { ..._resolved, ..._existing }
      }
    }
  }

  // ── Apply ────────────────────────────────────────────────────────────────
  // The framework's own switch, invoked WITH an element so `this.context` and
  // `this.state.root` resolve: it writes `context.state.lang`, updates the root
  // state (which is what makes every reactive prop re-evaluate) and seeds the
  // language's keys into root state. Bare, as it used to be called, it returns
  // at `if (!root) return` and does nothing.
  //
  // It also writes `localStorage[storageLangKey]` unconditionally, which a
  // `transient` switch must not do — hence the snapshot/restore. A transient
  // switch runs pre-mount today (bootShell, no element yet) so the branch is
  // belt-and-braces, not the live path.
  if (el && context?.polyglot) {
    const store = typeof localStorage !== 'undefined' ? localStorage : null
    const before = o.transient ? (store?.getItem?.('lang') ?? null) : null
    // `redeliver` — push the CURRENT bundles into an already-mounted tree.
    //
    // The plugin's `setLang` returns immediately when `getActiveLang()` already
    // equals the target, and at boot it always does: the shell mints polyglot
    // with the stored language BEFORE the bundles for that language exist, then
    // loads and merges them a step later. Nothing then pushes the merged
    // strings into the mounted tree, so whichever surface rendered first keeps
    // the English it was born with. Measured live on 2026-09-03: the same
    // tickets board came up Georgian on one boot and English on the next, and
    // the App Store came up English with all 458 of its Georgian keys sitting
    // in the context — decided purely by render order.
    //
    // `getActiveLang` reads `root.lang` then `context.state.lang`. Blanking
    // both, synchronously and with no update between, makes the plugin run its
    // full pass: it writes every translation into root state and calls
    // `root.update`, and that is what re-evaluates a function-form prop.
    if (o.redeliver) {
      const _root = el.state?.root || el.state
      if (_root && _root.lang) _root.lang = ''
      if (context.state && context.state.lang) context.state.lang = ''
    }
    const applied = polyglotSetLang.call(el, newLang)
    // The framework writes storage synchronously. Restore a transient choice
    // before yielding, so its continuation cannot overwrite a newer choice.
    if (o.transient && store) {
      if (before === null) store.removeItem('lang')
      else store.setItem('lang', before)
    }
    await applied
    if (!isCurrent()) return false
  } else if (shellState) {
    // No element (boot, cross-tab sync): seed the state the mount will adopt.
    if (typeof shellState.update === 'function') shellState.update({ lang: newLang })
    else shellState.lang = newLang
  }
  if (context?.state && context.state.lang !== newLang) context.state.lang = newLang

  // ── Re-title the routed page ─────────────────────────────────────────────
  // `<title>` is written by the helmet plugin from the routed page's
  // `metadata` (`pageTitle` → `polyglot`). Its re-apply on a language change
  // rides a signal effect inside `@symbo.ls/helmet`, and that package imports
  // `@symbo.ls/signal` without declaring it — so whether the effect re-fires
  // depends on which signal copy the served bundle resolves. Measured live on
  // my.symbols.app (2026-09-23): after the policy's
  // `setLang('ka', { transient, redeliver })` the tree and `<html lang>` were
  // Georgian and `<title>` still read "Home".
  //
  // So the switch re-titles the routed page itself, through the plugin's own
  // `update` hook — the framework's documented explicit re-apply (resolve the
  // element's `metadata`, apply it to the document), with no import and no
  // re-render. Idempotent where the effect does fire: it writes the same
  // title again.
  //
  // Skipped while a canvas PageSlot pane is open (`root._canvasSlot`): the
  // slot's primary pane owns the title then (canvas PageSlot
  // `_syncSlotTitle`), and re-titling the page behind it would clobber that.
  if (el && context) {
    try {
      const _appRoot = typeof el.getRoot === 'function' ? el.getRoot() : null
      const _appState = _appRoot?.state?.root || _appRoot?.state || null
      const _page = _appRoot ? _appRoot[_appRoot.__ref?.contentElementKey || 'content'] : null
      const _plugins = _appRoot?.context?.plugins || context.plugins || []
      const _helmet = _plugins.find((p) => p && p.name === 'helmet')
      if (
        _page &&
        _page.metadata &&
        _helmet &&
        typeof _helmet.update === 'function' &&
        !(_appState && _appState._canvasSlot)
      ) {
        _helmet.update(_page)
      }
    } catch (err) {
      console.warn(`[setLang] could not re-title the page: ${err?.message || err}`)
    }
  }

  if (!o.transient && typeof localStorage !== 'undefined') {
    localStorage.setItem('lang', newLang)
  }

  // FA206 — dynamic import, NOT a module-scope `import { queryClient }`.
  // `@symbo.ls/fetch` matches frank's `EXTERNAL_PACKAGES` (`'@symbo.ls/*'`),
  // so `stubExternalsPlugin` replaces it with a `stub-external:` shim at
  // serialization. `setLang` is an exported runtime primitive — a language
  // switcher calls it long after boot — so a module-scope binding resolves to
  // the stub and `dropNonLang` silently does nothing at any frank-served
  // runtime: stale non-`en` query cache survives the switch, with no error.
  // Importing at call time gets the real module in both local dev and prod.
  // Destructure defensively for ESM/CJS shape variance (FA206), but call
  // UNGUARDED: a genuinely missing export must still throw, exactly as the
  // old module-scope binding did. This whole ticket is about breakage that
  // was silent — swallowing it behind `?.` would reintroduce the defect in
  // a new place.
  // The IMPORT ITSELF has to be guarded, not just the value it yields. The
  // landing site and the docs site both declare `system/shared` as a shared
  // library and define no `setLang` of their own, so `mergeSharedLibraries`
  // hands them THIS function and `el.call('setLang', …)` runs it — on a site
  // that has no `@symbo.ls/fetch` in its import map at all. Measured live on
  // the landing at 13:02 by the landing session: "TypeError: Failed to resolve
  // module specifier '@symbo.ls/fetch' at Object.setLang". The rejection came
  // from the `await import(...)` on the line below, BEFORE the guard beneath
  // it could do anything, so guarding only the value left the throw in place.
  let fetchMod = null
  try {
    fetchMod = await import('@symbo.ls/fetch')
  } catch (err) {
    console.warn(
      `[setLang] '@symbo.ls/fetch' did not resolve (${err?.message || err}) — no ` +
        'query cache to evict on this surface. Language switched anyway.'
    )
  }
  if (!isCurrent()) return false
  const queryClient = fetchMod?.queryClient || fetchMod?.default?.queryClient
  // Loud, but not fatal. The original rule here was to call UNGUARDED so a
  // genuinely missing export still throws — the whole point of that ticket was
  // that the breakage had been SILENT. A warning is not silent, and a throw at
  // THIS point is actively worse for the reader: every earlier step has already
  // run, so the document language, the bundles, the root state and the stored
  // preference are all switched and only the stale query cache is left — the
  // UI would be in the new language with an uncaught error in the console.
  // It also has to survive off-shell: a frank-published consumer gets a stub
  // for `@symbo.ls/*`, and a project with no fetch plugin has no query cache to
  // evict in the first place.
  if (queryClient && typeof queryClient.dropNonLang === 'function') {
    queryClient.dropNonLang('en')
  } else {
    console.warn(
      '[setLang] @symbo.ls/fetch exposed no usable queryClient — the stale ' +
        'non-English query cache was NOT evicted. Language switched anyway.'
    )
  }

  // The account write — a cross-device record of a REAL choice. Skipped for a
  // resolved language (`transient`) and for the cross-tab echo (`skipPersist`),
  // where the originating tab already wrote it.
  // `el.sdk` is the established element-level accessor in this package
  // (AppOrgSwitcher, AiSettingsPanel); nothing ever sets `context.sdk`, so the
  // switcher path would have no SDK at all without it.
  const sdk = o.sdk || context?.sdk || el?.sdk || null
  const user = shellState?.user || context?.state?.user || null
  if (!o.transient && !o.skipPersist && sdk && user) {
    // SDK proxies AuthService methods flat (sdk.updateMe). There is no
    // sdk.auth namespace — see sdk/src/utils/services.js SERVICE_METHODS.
    if (typeof sdk.updateMe === 'function') {
      // Guarded on purpose. By the time this runs the switch has ALREADY
      // succeeded locally — document, bundles, root state and this device's
      // stored preference are all on the new language. An unguarded await
      // rejects into an onClick handler that has no catch, so a dropped
      // connection or an expired session would surface as an unhandled
      // rejection while the interface sits happily in the new language.
      //
      // Reverting would be worse than failing to persist: the person asked for
      // this language, they got it, and the only thing lost is that the choice
      // does not follow them to another device. So keep the switch, and say so
      // loudly rather than silently.
      //
      // The server's `400 invalid_language` cannot reach here through the UI —
      // the shipped-language guard at the top of this function refuses an
      // unshipped code before anything happens. Any rejection here is a real
      // transport or auth failure, not a bad value.
      // Serialize account writes, not UI updates: an older slow save must not
      // arrive after the latest selection and become the next device's choice.
      const previous = requestOwner?._languagePersistTask || Promise.resolve()
      const persist = previous
        .catch(() => {})
        .then(async () => {
          if (!isCurrent()) return
          try {
            await sdk.updateMe({ preferences: { lang: newLang } })
          } catch (err) {
            console.warn(
              `[setLang] switched to '${newLang}' on this device, but saving it to ` +
                'the account failed — the choice will not follow you to another ' +
                `device: ${err?.message || err}`
            )
          }
        })
      if (requestOwner) requestOwner._languagePersistTask = persist
      await persist
      if (requestOwner?._languagePersistTask === persist) delete requestOwner._languagePersistTask
    }
  }

  return true
}
